'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from '@/lib/router';
import {
  BuildingsIcon,
  TrendUpIcon,
  CheckIcon,
  PiggyBankIcon,
  ShieldIcon,
  CreditCardIcon,
  CalendarIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useFinance } from '@/hooks/useFinance';
import { track } from '@/lib/telemetry';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import type { LinkableEntity, GoalType } from '@/types';
import { TypePicker } from './metas/TypePicker';
import { CreatePreview } from './metas/CreatePreview';
import { MoneyInput, parseMoneyInput as parseMoney } from '@/components/ui/MoneyInput';

const TYPE_META: Record<GoalType, { label: string; Icon: React.ElementType; iconBg: string; iconColor: string }> = {
  Savings: { label: 'Poupança', Icon: PiggyBankIcon, iconBg: 'bg-success-surface', iconColor: 'text-success-text' },
  Reserve: { label: 'Reserva', Icon: ShieldIcon, iconBg: 'bg-info-surface', iconColor: 'text-info-text' },
  DebtPayoff: { label: 'Dívida', Icon: CreditCardIcon, iconBg: 'bg-error-surface', iconColor: 'text-error-text' },
};

export function MetaCreateView() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { fetchLinkableEntities, fetchGoalsOverview, createGoal } = useFinance();
  const formatCurrency = useFormatCurrency();

  const initialType = (searchParams.get('type') as GoalType | null) ?? null;
  const [goalType, setGoalType] = useState<GoalType | null>(initialType);

  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');

  // Reserve
  const [reserveMonths, setReserveMonths] = useState(6);
  const [essentialMonthly, setEssentialMonthly] = useState(0);
  const [averageMonthlyExpense, setAverageMonthlyExpense] = useState(0);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customReserveAmount, setCustomReserveAmount] = useState('');
  const [reserveAlreadyExists, setReserveAlreadyExists] = useState(false);

  // Debt
  const [initialBalance, setInitialBalance] = useState('');
  const [currentBalance, setCurrentBalance] = useState('');
  const [interestRateAnnual, setInterestRateAnnual] = useState('');

  const [linkableEntities, setLinkableEntities] = useState<LinkableEntity[]>([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(new Set());
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [entities, overview] = await Promise.all([
        fetchLinkableEntities(),
        fetchGoalsOverview().catch(() => null),
      ]);
      setLinkableEntities(entities);
      if (overview?.essentialMonthly != null) {
        setEssentialMonthly(overview.essentialMonthly);
      }
      if (overview?.averageMonthlyExpense != null) {
        setAverageMonthlyExpense(overview.averageMonthlyExpense);
      }
      if (overview?.goals?.some((g) => g.type === 'Reserve')) {
        setReserveAlreadyExists(true);
        setGoalType((current) => (current === 'Reserve' ? null : current));
      }
    } finally {
      setInitialLoaded(true);
    }
  }, [fetchLinkableEntities, fetchGoalsOverview]);

  useEffect(() => {
    track('action.goal.create_start');
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // Default name when picking Reserve
    if (goalType === 'Reserve' && !name) setName('Reserva de Emergência');
    if (goalType === 'DebtPayoff' && currentBalance === '' && initialBalance !== '') {
      setCurrentBalance(initialBalance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalType]);

  const toggleEntity = (id: string) => {
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const parsedTargetAmount = parseMoney(targetAmount);
  const parsedMonthlyContribution = parseMoney(monthlyContribution);
  const parsedInitialBalance = parseMoney(initialBalance);
  const parsedCurrentBalance = parseMoney(currentBalance);
  const parsedInterestRate = parseFloat(interestRateAnnual.replace(',', '.')) || 0;

  const startingBalance = useMemo(() => {
    return linkableEntities
      .filter((e) => selectedEntityIds.has(e.id))
      .reduce((sum, e) => sum + e.balance, 0);
  }, [linkableEntities, selectedEntityIds]);

  // Suggestion based on actual 6-month expense average; falls back to budget limits.
  const reserveMonthlyBase = averageMonthlyExpense > 0 ? averageMonthlyExpense : essentialMonthly;
  const reserveSuggestion = reserveMonths * reserveMonthlyBase;
  const parsedCustomReserveAmount = parseMoney(customReserveAmount);
  const effectiveReserveTarget =
    useCustomAmount && parsedCustomReserveAmount > 0 ? parsedCustomReserveAmount : reserveSuggestion;
  // Keep for backward compat with CreatePreview prop (passes avg-based amount)
  const reserveTarget = effectiveReserveTarget;

  const debtMonthlyRate = parsedInterestRate / 100 / 12;
  const debtMonthlyInterest = parsedCurrentBalance * debtMonthlyRate;
  const debtPaymentTooLow =
    goalType === 'DebtPayoff' &&
    parsedCurrentBalance > 0 &&
    parsedMonthlyContribution > 0 &&
    parsedMonthlyContribution <= debtMonthlyInterest;

  const canSave = useMemo(() => {
    if (!goalType || !name.trim()) return false;
    if (goalType === 'Savings') {
      if (parsedTargetAmount <= 0) return false;
      if (parsedMonthlyContribution > 0 && parsedMonthlyContribution >= parsedTargetAmount) return false;
      if (targetDate && targetDate < new Date().toISOString().split('T')[0]) return false;
    }
    if (goalType === 'Reserve') {
      if (reserveMonths <= 0) return false;
      if (effectiveReserveTarget <= 0) return false;
    }
    if (goalType === 'DebtPayoff') {
      if (parsedInitialBalance <= 0) return false;
      if (parsedCurrentBalance < 0) return false;
      if (parsedCurrentBalance > parsedInitialBalance) return false;
      if (debtPaymentTooLow) return false;
    }
    return true;
  }, [
    goalType,
    name,
    parsedTargetAmount,
    parsedMonthlyContribution,
    targetDate,
    reserveMonths,
    effectiveReserveTarget,
    parsedInitialBalance,
    parsedCurrentBalance,
    debtPaymentTooLow,
  ]);

  const handleSave = async () => {
    if (!canSave || !goalType) return;
    setSaving(true);
    try {
      const linkedEntities = Array.from(selectedEntityIds).flatMap(
        (id): { accountId?: string; investmentId?: string }[] => {
          const entity = linkableEntities.find((e) => e.id === id);
          if (!entity) return [];
          return entity.type === 'account' ? [{ accountId: id }] : [{ investmentId: id }];
        },
      );

      const finalTarget =
        goalType === 'Reserve'
          ? reserveTarget
          : goalType === 'DebtPayoff'
            ? parsedInitialBalance
            : parsedTargetAmount;

      await createGoal({
        name: name.trim(),
        targetAmount: finalTarget,
        targetDate: targetDate || undefined,
        monthlyContribution: parsedMonthlyContribution || undefined,
        linkedEntities: goalType === 'DebtPayoff' ? [] : linkedEntities,
        type: goalType,
        targetMonths: goalType === 'Reserve' ? reserveMonths : undefined,
        initialBalance: goalType === 'DebtPayoff' ? parsedInitialBalance : undefined,
        currentBalance: goalType === 'DebtPayoff' ? parsedCurrentBalance : undefined,
        interestRateAnnual:
          goalType === 'DebtPayoff' && parsedInterestRate > 0 ? parsedInterestRate : undefined,
      });
      track('action.goal.create', {
        type: goalType,
        linked_entities_count: goalType === 'DebtPayoff' ? 0 : linkedEntities.length,
      });
      router.push('/metas');
    } catch {
      track('goal.create_failed');
      // hook handles error toast
    } finally {
      setSaving(false);
    }
  };

  if (!initialLoaded) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-7">
            <Skeleton className="h-64 rounded-2xl" />
            <Skeleton className="h-64 rounded-2xl" />
          </div>
          <Skeleton className="h-96 rounded-2xl lg:col-span-5" />
        </div>
      </div>
    );
  }

  // ── Step 1: type picker ─────────────────────────────────────────────────────
  if (!goalType) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <h1 className="text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
          Vamos planejar sua meta
        </h1>
        <TypePicker onSelect={setGoalType} reserveAlreadyExists={reserveAlreadyExists} />
      </div>
    );
  }

  // ── Step 2: type-aware form ─────────────────────────────────────────────────
  const tm = TYPE_META[goalType];
  const TmIcon = tm.Icon;
  const showAssetLinking = goalType !== 'DebtPayoff';

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
            Vamos planejar sua meta
          </h1>
          <button
            type="button"
            onClick={() => setGoalType(null)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border-subtle bg-surface hover:border-border-strong transition`}
          >
            <span className={`size-6 rounded-lg flex items-center justify-center ${tm.iconBg} ${tm.iconColor}`}>
              <TmIcon className="size-3.5" weight="duotone" />
            </span>
            <span className="text-xs font-semibold text-text-primary">{tm.label}</span>
            <span className="text-2xs text-text-tertiary">trocar</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12 lg:gap-10">
        {/* Left: Form */}
        <div className="order-2 space-y-6 sm:space-y-8 lg:order-1 lg:col-span-7">
          <div className="space-y-5">
            <h3 className="text-lg font-bold text-text-primary">Detalhes da meta</h3>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="col-span-1 md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-text-primary">Nome</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={
                    goalType === 'Reserve'
                      ? 'Reserva de Emergência'
                      : goalType === 'DebtPayoff'
                        ? 'Quitar cartão Nubank'
                        : 'Viagem ao Japão'
                  }
                  className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                />
              </div>

              {goalType === 'Savings' && (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-text-primary">Valor alvo (R$)</label>
                    <MoneyInput
                      value={targetAmount}
                      onChange={(formatted) => setTargetAmount(formatted)}
                      placeholder="50.000,00"
                      className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary placeholder:font-normal placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-text-primary">Data alvo</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className={`w-full flex items-center justify-between bg-surface border border-border rounded-xl px-4 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary ${targetDate ? 'text-text-primary' : 'text-text-tertiary'}`}
                        >
                          <span>
                            {targetDate
                              ? format(new Date(targetDate + 'T12:00:00'), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                              : 'Selecionar data...'}
                          </span>
                          <CalendarIcon size={16} className="shrink-0 text-text-tertiary" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" sideOffset={6} className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={targetDate ? new Date(targetDate + 'T12:00:00') : undefined}
                          onSelect={(date) => { if (date) setTargetDate(format(date, 'yyyy-MM-dd')); }}
                          disabled={{ before: new Date() }}
                          locale={ptBR}
                          numberOfMonths={1}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <label className="mb-2 block text-sm font-semibold text-text-primary">Aporte mensal (R$)</label>
                    <MoneyInput
                      value={monthlyContribution}
                      onChange={(formatted) => setMonthlyContribution(formatted)}
                      placeholder="1.000,00"
                      className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary placeholder:font-normal placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                    {parsedMonthlyContribution > 0 && parsedMonthlyContribution >= parsedTargetAmount && (
                      <p className="mt-1 text-xs text-error">O aporte deve ser menor que o valor alvo.</p>
                    )}
                  </div>
                </>
              )}

              {goalType === 'Reserve' && (
                <>
                  {/* Months slider */}
                  <div className="col-span-1 md:col-span-2">
                    <div className="mb-2 flex items-center justify-between">
                      <label className="text-sm font-semibold text-text-secondary">Meses de cobertura</label>
                      <span className="font-mono text-sm font-bold text-info-text">
                        {reserveMonths} {reserveMonths === 1 ? 'mês' : 'meses'}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={24}
                      value={reserveMonths}
                      onChange={(e) => setReserveMonths(parseInt(e.target.value, 10))}
                      className="w-full accent-sky-600"
                    />
                    <div className="mt-1 flex justify-between font-mono text-2xs text-text-tertiary">
                      <span>1m</span>
                      <span>6m</span>
                      <span>12m</span>
                      <span>24m</span>
                    </div>
                  </div>

                  {/* Target amount card: suggestion + optional custom override */}
                  <div className="col-span-1 rounded-xl border border-info-border bg-info-surface/60 p-4 md:col-span-2">
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <p className="text-2xs font-bold tracking-wider text-text-tertiary uppercase">
                        {useCustomAmount ? 'Valor alvo (personalizado)' : 'Valor alvo (sugerido)'}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          if (useCustomAmount) {
                            setUseCustomAmount(false);
                            setCustomReserveAmount('');
                          } else {
                            setUseCustomAmount(true);
                            // Pre-fill the input with the current suggestion
                            if (reserveSuggestion > 0) {
                              const [int, dec] = reserveSuggestion.toFixed(2).split('.');
                              setCustomReserveAmount(
                                int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec,
                              );
                            }
                          }
                        }}
                        className="shrink-0 text-2xs font-medium text-info-text hover:text-info-text"
                      >
                        {useCustomAmount ? 'Usar sugestão' : 'Personalizar'}
                      </button>
                    </div>

                    {useCustomAmount ? (
                      <MoneyInput
                        value={customReserveAmount}
                        onChange={(formatted) => setCustomReserveAmount(formatted)}
                        placeholder="18.000,00"
                        className="w-full rounded-xl border border-info-border bg-surface py-3 pr-4 font-mono text-text-primary placeholder:font-normal placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-info"
                      />
                    ) : (
                      <>
                        <p className="font-mono text-2xl font-bold text-text-primary tabular-nums">
                          {formatCurrency(reserveSuggestion)}
                        </p>
                        <p className="mt-1 text-2xs text-text-secondary">
                          {reserveMonths} {reserveMonths === 1 ? 'mês' : 'meses'} ×{' '}
                          <span className="font-mono">{formatCurrency(reserveMonthlyBase)}</span>/mês
                          {averageMonthlyExpense > 0
                            ? <> · média dos últimos 6 meses</>
                            : essentialMonthly > 0
                              ? <> · baseado no seu orçamento</>
                              : <> — conecte suas contas para cálculo automático</>}
                        </p>
                      </>
                    )}
                  </div>

                  {/* Monthly contribution */}
                  <div className="col-span-1 md:col-span-2">
                    <label className="mb-2 block text-sm font-semibold text-text-primary">Aporte mensal (R$)</label>
                    <MoneyInput
                      value={monthlyContribution}
                      onChange={(formatted) => setMonthlyContribution(formatted)}
                      placeholder="500,00"
                      className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary placeholder:font-normal placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                  </div>
                </>
              )}

              {goalType === 'DebtPayoff' && (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-text-primary">Saldo inicial (R$)</label>
                    <MoneyInput
                      value={initialBalance}
                      onChange={(formatted) => {
                        setInitialBalance(formatted);
                        if (!currentBalance) setCurrentBalance(formatted);
                      }}
                      placeholder="10.000,00"
                      className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-text-primary">Saldo atual (R$)</label>
                    <MoneyInput
                      value={currentBalance}
                      onChange={(formatted) => setCurrentBalance(formatted)}
                      placeholder="6.500,00"
                      className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-text-primary">Juros anual (%, opcional)</label>
                    <input
                      type="text"
                      value={interestRateAnnual}
                      onChange={(e) => setInterestRateAnnual(e.target.value)}
                      placeholder="18"
                      className="w-full rounded-xl border border-border bg-surface px-4 py-3 font-mono text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-text-primary">Pagamento mensal (R$)</label>
                    <MoneyInput
                      value={monthlyContribution}
                      onChange={(formatted) => setMonthlyContribution(formatted)}
                      placeholder="800,00"
                      className="w-full rounded-xl border border-border bg-surface py-3 pr-4 font-mono text-text-primary placeholder:font-normal placeholder:text-text-tertiary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    />
                    {debtPaymentTooLow && (
                      <p className="mt-1 text-xs text-error">
                        O pagamento deve cobrir os juros mensais (≈ {formatCurrency(debtMonthlyInterest)}).
                      </p>
                    )}
                  </div>
                  <div className="col-span-1 rounded-xl border border-error-border bg-error-surface/60 p-3 text-xs/relaxed text-text-secondary md:col-span-2">
                    O progresso desta meta é calculado como <strong>saldo inicial − saldo atual</strong>. Não vinculamos contas para dívidas.
                  </div>
                </>
              )}
            </div>
          </div>

          {showAssetLinking && (
            <div className="space-y-5">
              <h3 className="text-lg font-bold text-text-primary">Vincular ativos (opcional)</h3>
              <div className="rounded-2xl border border-border-subtle bg-surface-sunken p-4 sm:p-6">
                <p className="mb-5 text-sm/relaxed text-text-secondary">
                  O progresso será rastreado{' '}
                  <span className="font-semibold text-success-text">automaticamente</span> a partir do saldo das entidades selecionadas.
                </p>
                {linkableEntities.length === 0 ? (
                  <p className="py-8 text-center text-sm text-text-tertiary">Nenhuma conta ou investimento disponível.</p>
                ) : (
                  <div className="space-y-3">
                    {linkableEntities.map((entity) => {
                      const isChecked = selectedEntityIds.has(entity.id);
                      const isTakenByOther = entity.linkedGoalId !== null;
                      const isDisabled = isTakenByOther && !isChecked;
                      return (
                        <button
                          key={entity.id}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => { if (!isDisabled) toggleEntity(entity.id); }}
                          className={`w-full text-left flex items-center justify-between gap-3 p-3 sm:p-4 bg-surface rounded-xl transition-all ${
                            isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-none'
                          } ${isChecked ? 'ring-2 ring-success/30' : ''}`}
                          aria-pressed={isChecked}
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                            <div className={`size-10 shrink-0 rounded-lg flex items-center justify-center ${
                              entity.type === 'account' ? 'bg-purple-100 text-purple-600' : 'bg-error-surface text-error-text'
                            }`}>
                              {entity.type === 'account' ? <BuildingsIcon className="size-5" /> : <TrendUpIcon className="size-5" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-bold text-text-primary">{entity.name}</p>
                              <p className="truncate font-mono text-xs text-text-secondary">
                                {formatCurrency(entity.balance)}
                                {entity.institution && ` • ${entity.institution}`}
                              </p>
                              {isTakenByOther && entity.linkedGoalName && (
                                <p className="mt-0.5 truncate text-xs text-warning-text">
                                  Vinculado à meta: {entity.linkedGoalName}
                                </p>
                              )}
                            </div>
                          </div>
                          <span
                            className={`size-5 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                              isChecked
                                ? 'bg-success border-success-border'
                                : isDisabled
                                  ? 'border-border-subtle bg-muted'
                                  : 'border-border-strong'
                            }`}
                            aria-hidden
                          >
                            {isChecked && <CheckIcon className="size-3 text-success-foreground" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center">
            <Button
              size="lg"
              onClick={handleSave}
              disabled={!canSave || saving}
              className="w-full sm:w-auto"
            >
              {saving ? 'Criando...' : 'Criar meta'}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => router.push('/metas')}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
          </div>
        </div>

        {/* Right: Preview */}
        <aside className="order-1 lg:sticky lg:top-8 lg:order-2 lg:col-span-5">
          <CreatePreview
            goalType={goalType}
            name={name}
            targetAmount={parsedTargetAmount}
            monthlyContribution={parsedMonthlyContribution}
            startingBalance={startingBalance}
            reserveMonths={reserveMonths}
            essentialMonthly={reserveMonthlyBase}
            debtBalance={parsedCurrentBalance || parsedInitialBalance}
            debtPayment={parsedMonthlyContribution}
            debtAnnualRate={parsedInterestRate / 100}
          />
        </aside>
      </div>
    </div>
  );
}
